import { useContext, useState } from "react";
import { Markdown } from "../markdown";
import type { ComponentProps } from "./index";
import { useWorkspaceExists, useWorkspaceText, workspaceUrl } from "./useWorkspaceJson";
import { isScriptedPage } from "../../../shared/scripted-page/suffix.ts";
import { ChatSend } from "./chatSend";
import { documentAction, type DocumentAction } from "./documentAction";

type Kind = "pdf" | "markdown" | "html" | "other";

function kindOf(source: string): Kind {
	const dot = source.lastIndexOf(".");
	const ext = dot === -1 ? "" : source.slice(dot).toLowerCase();
	if (ext === ".pdf") return "pdf";
	if (ext === ".md") return "markdown";
	if (ext === ".html" || ext === ".htm") return "html";
	return "other";
}

function basename(source: string): string {
	const slash = source.lastIndexOf("/");
	return slash === -1 ? source : source.slice(slash + 1);
}

/**
 * Renders `{ source, title?, action? }` by extension, with an optional badge under the title.
 *
 * `.html` is agent-generated and, in this profile, can derive from job postings — untrusted
 * third-party data by design. It goes in a sandboxed <iframe> so it never gains this app's
 * origin, storage, or cookies, matching the workspace route's own `Content-Security-Policy:
 * sandbox` on the same file (this is defence in depth, not a substitute for that header).
 */
export function Document({ sessionId, args, badge }: ComponentProps & { badge?: React.ReactNode }): React.ReactNode {
	const source = typeof args.source === "string" ? args.source : undefined;
	const title = typeof args.title === "string" ? args.title : undefined;
	// A companion artefact to offer beside the rendered document, e.g. the PDF a
	// report was also rendered to. Optional; nothing appears when it is absent.
	const download = typeof args.download === "string" ? args.download : undefined;

	if (!source) {
		return <div className="component error">document 组件缺少 source 参数，收到的参数：{JSON.stringify(args)}</div>;
	}

	const kind = kindOf(source);
	if (kind === "markdown")
		return <MarkdownDocument sessionId={sessionId} source={source} title={title} download={download} badge={badge} />;
	return <FileDocument sessionId={sessionId} source={source} title={title} kind={kind} action={documentAction(args.action)} badge={badge} />;
}

/**
 * Sends the action's command as the user's message. Off while nothing can be sent, and for good once
 * pressed: the reply that follows shows the next card.
 */
function ActionButton({ action }: { action: DocumentAction }) {
	const send = useContext(ChatSend);
	const [pressed, setPressed] = useState(false);
	const [refused, setRefused] = useState<string | null>(null);
	return (
		<>
			<button
				className="document-action"
				disabled={!send || pressed}
				onClick={async () => {
					if (!send) return;
					setPressed(true);
					const why = await send(action.message);
					setRefused(why);
					if (why) setPressed(false);
				}}
			>
				{action.label}
			</button>
			{refused && <span className="error">{refused}</span>}
		</>
	);
}

function MarkdownDocument({ sessionId, source, title, download, badge }: { sessionId: string; source: string; title: string | undefined; download?: string; badge?: React.ReactNode }) {
	const { data, error, loading } = useWorkspaceText(sessionId, source);
	return (
		<div className="component document-component">
			{title && <div className="document-title">{title}</div>}
			{badge}
			{loading && <p className="hint">加载中…</p>}
			{error && <p className="error">{error}</p>}
			{data !== null && <Markdown text={data} />}
			{download && <CompanionDownload sessionId={sessionId} source={download} />}
		</div>
	);
}

/** A companion file offered beside a rendered document -- the PDF next to the
 * Markdown a report was written as. The renderer is handed the path it should
 * show; without this it has no way to know a PDF exists beside it, so the user
 * reads the report and cannot take the artefact. Absent or missing on disk, the
 * link simply does not appear. */
function CompanionDownload({ sessionId, source }: { sessionId: string; source: string }) {
	const { data: exists } = useWorkspaceExists(sessionId, source);
	if (!exists) return null;
	return (
		<a className="document-download" href={workspaceUrl(sessionId, source)} download={basename(source)}>
			下载 {basename(source)}
		</a>
	);
}

/**
 * The frame is a preview, not a reading surface: a deck is written 900px wide and runs a whole sitting
 * long, and the frame gives it 60vh of a chat column. The same URL in a tab is the whole page.
 *
 * Safe as a top-level document because the workspace route stamps `content-security-policy: sandbox` on
 * the HTML it serves. That header, not the `sandbox=""` attribute on the iframe, is what denies the page
 * this app's origin, storage and cookies, and it applies in a tab exactly as it does in a frame.
 */
function OpenInTab({ url }: { url: string }) {
	return (
		<a className="document-download" href={url} target="_blank" rel="noreferrer noopener">
			在新标签页打开
		</a>
	);
}

/** `.pdf` previews inline plus links; `.html` is a sandboxed preview plus a link; anything else is download-only. */
function FileDocument({ sessionId, source, title, kind, action, badge }: { sessionId: string; source: string; title: string | undefined; kind: Kind; action: DocumentAction | null; badge?: React.ReactNode }) {
	// A missing file behind a bare <iframe src> would just render the route's JSON 404 body
	// inside the frame — visible if you squint, but it neither reads as an error nor names the
	// path. Checking first (HEAD, no body downloaded) lets a real 404/403 say so plainly instead.
	const { data: exists, error, loading } = useWorkspaceExists(sessionId, source);
	const url = workspaceUrl(sessionId, source);
	const name = basename(source);

	return (
		<div className="component document-component">
			{title && <div className="document-title">{title}</div>}
			{badge}
			{loading && <p className="hint">加载中…</p>}
			{error && <p className="error">{error}</p>}
			{exists && kind === "pdf" && (
				<>
					<iframe className="document-frame" src={url} title={title ?? source} />
					<div className="document-links">
						<OpenInTab url={url} />
						<a className="document-download" href={url} download={name}>
							下载
						</a>
					</div>
				</>
			)}
			{exists && kind === "html" && (
				<>
					{/* A lesson page runs its own quiz or simulator, so the frame has to let it; every
					    other page keeps the empty sandbox. Both layers have to agree — the header alone
					    covers a tab, this attribute alone covers the frame — and neither grants
					    allow-same-origin, so the page stays on an opaque origin either way. */}
					<iframe className={isScriptedPage(source) ? "document-frame lesson-frame" : "document-frame"} src={url} sandbox={isScriptedPage(source) ? "allow-scripts" : ""} title={title ?? source} />
					<div className="document-links">
						<OpenInTab url={url} />
						{action && <ActionButton action={action} />}
					</div>
				</>
			)}
			{exists && kind === "other" && (
				<a className="document-download" href={url} download={name}>
					下载 {name}
				</a>
			)}
		</div>
	);
}
